import type { AppDispatch, RootState } from '../store';
import axios, { AxiosError } from 'axios';
import api from '../../api';  

import {
  USER_SIGNIN_FAIL,
  USER_SIGNIN_REQUEST,
  USER_SIGNIN_SUCCESS,
  USER_SIGNIN_SIGNOUT,
  USER_REGISTER_REQUEST,
  USER_REGISTER_SUCCESS,
  USER_REGISTER_FAIL,
  USER_DETAILS_SUCCESS,
  USER_DETAILS_FAIL,
  USER_DETAILS_REQUEST,
  USER_UPDATE_PROFILE_REQUEST,
  USER_UPDATE_PROFILE_SUCCESS,
  USER_UPDATE_PROFILE_FAIL,
} from '../constant/userConstant';

// ----- Tipos --------------------------------------------------------------------------------------------
interface UserProfile {
  _id?: string; // opcional para actualizar el perfil, no se envía al backend
  name: string;
  email: string;
  password?: string;
  // agrega los campos que envias al actualizar el perfil
}

interface UserInfo {
  _id: string;
  name: string;
  email: string;
  token: string;
  // agrega los campos que recibes al iniciar sesión
}

// Tipos de cada action posible
interface UpdateProfileRequestAction {
  type: typeof USER_UPDATE_PROFILE_REQUEST;
  payload: UserProfile;
}

interface UpdateProfileSuccessAction {
  type: typeof USER_UPDATE_PROFILE_SUCCESS;
  payload: UserInfo;
}

interface UpdateProfileFailAction {
  type: typeof USER_UPDATE_PROFILE_FAIL;
  payload: string; // mensaje de error
}

interface UserSigniniSuccessAction {
  type: typeof USER_SIGNIN_SUCCESS;
  payload: UserInfo;
}

// Union  type de todas las acciones de este thunk
type UserUdateProfileActions =
  | UpdateProfileRequestAction
  | UpdateProfileSuccessAction
  | UpdateProfileFailAction
  | UserSigniniSuccessAction;

// ----- Actions  creator --------------------------------------------------------------------------------------------

/**
 * @action updateUserProfile
 * @description Actualiza el perfil del usuario autenticado.
 *
 * ┌──────────────────────────────────────────────────────────────────┐
 * │                        Flujo
 * │
 * │  dispatch REQUEST ──► PUT /users/profile ──► dispatch SUCCESS
 * │                                         └──► dispatch SIGNIN
 * │                                         └──► localStorage.set
 * │                              error ──► dispatch FAIL
 * └──────────────────────────────────────────────────────────────────┘
 *
 * @param user - Datos del perfil a actualizar
 */

export const signin =
  (email: string, password: string) => async (dispatch: any) => {
 
    dispatch({ type: USER_SIGNIN_REQUEST, payload: { email, password } });

    try {
      const { data } = await api.post('/users/signin', { email, password });
      dispatch({ type: USER_SIGNIN_SUCCESS, payload: data });
      localStorage.setItem('userInfo', JSON.stringify(data));
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data.message || error.message
        : 'Ocurrió un error inesperado';
      dispatch({
        type: USER_SIGNIN_FAIL,
        payload:
          message === 'Not authorized, token failed' ? signout() : message,
      });
    }
  };

export const register = (name: string, email: string, password: string) => async (dispatch: any) => {
  /* continuar aqui */
  dispatch({ type: USER_REGISTER_REQUEST, payload: { name, email, password } });
  try {
    const { data } = await api.post('/users/register', {
      name,
      email,
      password,
    });
    dispatch({ type: USER_REGISTER_SUCCESS, payload: data });
    localStorage.setItem('userInfo', JSON.stringify(data));
  } catch (error) {
    const message = axios.isAxiosError(error)
      ? error.response?.data.message || error.message
      : 'Ocurrió un error inesperado';

    dispatch({
      type: USER_REGISTER_FAIL,
      payload: message === 'Not authorized, token failed' ? signout() : message,
    });
  }
};

export const detailsUser = (userId: string) => async (dispatch: any, getState: any) => {
  dispatch({ type: USER_DETAILS_REQUEST, payload: userId });

  const {
    userSignin: { userInfo },
  } = getState();

  try {
    const { data } = await api.get(`/users/${userId}`, {
      headers: {
        Authorization: `Bearer ${userInfo.token}`,
      },
    });

    dispatch({ type: USER_DETAILS_SUCCESS, payload: data });
  } catch (error) {
    const message = axios.isAxiosError(error)
      ? error.response?.data.message || error.message
      : 'Ocurrió un error inesperado';
    dispatch({
      type: USER_DETAILS_FAIL,
      payload: message === 'Not authorized, token failed' ? signout() : message,
    });
  }
};

export const updateUserProfile =
  (user: UserProfile) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    dispatch({ type: USER_UPDATE_PROFILE_REQUEST, payload: user });
    const {
      userSignin: { userInfo },
    } = getState() as any;

    try {
      const { data } = await api.put('/users/profile', user, {
        headers: { Authorization: `Bearer ${userInfo?.token}` },
      });

      dispatch({ type: USER_UPDATE_PROFILE_SUCCESS, payload: data });
      dispatch({ type: USER_SIGNIN_SUCCESS, payload: data });
      localStorage.setItem('userInfo', JSON.stringify(data));
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message = axiosError.response?.data?.message ?? axiosError.message;
      dispatch({
        type: USER_UPDATE_PROFILE_FAIL,
        payload: message,
      });
    }
  };

export const signout = () => (dispatch: any) => {
  localStorage.removeItem('cartItems');
  localStorage.removeItem('userInfo');
  localStorage.removeItem('shippingAddress');

  dispatch({ type: USER_SIGNIN_SIGNOUT });
};
